
import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { submitData } from "../slices/FbSections";

const checkIfDataExists = (data) => {
  if (!data || typeof data !== 'object') return false;

  const stringify = JSON.stringify(data);
  const hasNonEmptyValues = stringify.includes('"') &&
    stringify.replace(/[{}[\]":,]/g, '').trim().length > 0;
  return hasNonEmptyValues;
};


const findChanges = (originalObj, updatedObj) => {
  if (!originalObj?.sections || !updatedObj?.sections) {
    return { sections: [] };
  }

  const result = { sections: [] };

  // Create maps for easier lookup
  const originalSectionsMap = new Map();
  const originalFieldsMap = new Map();

  // Build maps from original object
  originalObj.sections.forEach(section => {
    originalSectionsMap.set(section?.section_uuid, section);

    section.fields?.forEach(field => {
      originalFieldsMap.set(field.input_uuid, field);
    });
  });

  // Check each section in updated object
  updatedObj.sections.forEach(updatedSection => {
    const originalSection = originalSectionsMap.get(updatedSection.section_uuid);

    // If section doesn't exist in original, it's new
    if (!originalSection) {
      result.sections.push({
        ...updatedSection
      });
      return;
    }

    const sectionChanged = !_.isEqual(updatedSection, originalSection);

    if (sectionChanged) {
      // Check fields for changes
      const changedFields = [];
      const newFields = [];
      updatedSection.fields?.forEach(updatedField => {
        const originalField = originalFieldsMap.get(updatedField.input_uuid);

        if (!originalField) {
          // Field is new
          newFields.push({
            ...updatedField
          });
        } else if (!_.isEqual(originalField, updatedField)) {
          // Field has changed
          changedFields.push({
            ...updatedField
          });
        }
      });

      const resultSection = {
        ...updatedSection
      };

      resultSection.fields = [...changedFields, ...newFields];
      result.sections.push(resultSection);
    }
  });

  return result;
};

export const useAutoSave = ({
  saveFunction,
  delay = 3000,
  enabled = true,
  onSaveStart,
  onSaveSuccess,
  onSaveError
}) => {
  const { control } = useFormContext();
  const watchedData = useWatch(control);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const prevScheema = useSelector(state => state.bmrFbSections.section)

  const formDataRef = useRef(watchedData);
  const dispatch = useDispatch()
  // Debounced save function using lodash
  const debouncedSave = useCallback(
    _.debounce(async (data) => {
      if (!enabled || !saveFunction) return;

      try {
        setSaveStatus('saving');
        setError(null);

        // Call optional callback
        if (onSaveStart) onSaveStart();
        const updatedScheema = findChanges(prevScheema, data);
        const result = await saveFunction(updatedScheema);
        if (result.data) {
          dispatch(submitData(data))
        }
        setSaveStatus('saved');
        setLastSaved(new Date());

        formDataRef.current = data; // Update formDataRef with latest data

        // Call optional success callback
        if (onSaveSuccess) onSaveSuccess(result);

        // Reset status after 3 seconds
        setTimeout(() => setSaveStatus('idle'), 2000); //@info increasing seconds

      } catch (err) {
        setSaveStatus('error');
        setError(err);

        // Call optional error callback
        if (onSaveError) onSaveError(err);
        // Reset status after 5 seconds
        setTimeout(() => setSaveStatus('idle'), 2000);
      }
    }, delay),
    [saveFunction, delay, enabled, onSaveStart, onSaveSuccess, onSaveError]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  // Effect to trigger autosave when data changes
  useEffect(() => {
    if (watchedData && enabled) {
      // Check if form has meaningful data
      const hasData = checkIfDataExists(watchedData);
      const prev = JSON.stringify(formDataRef.current);
      const current = JSON.stringify(watchedData);
      if (hasData && prev != current) {
        debouncedSave(watchedData);
      }
    }
  }, [watchedData]);

  // Manual save function
  const manualSave = async (data = watchedData) => {
    if (!saveFunction) return;

    try {
      setSaveStatus('saving');
      setError(null);

      if (onSaveStart) onSaveStart();

      const result = await saveFunction(data);

      setSaveStatus('saved');
      setLastSaved(new Date());

      if (onSaveSuccess) onSaveSuccess(result);

      return result;
    } catch (err) {
      setSaveStatus('error');
      setError(err);

      if (onSaveError) onSaveError(err);

      throw err;
    }
  };

  // Clear error function
  const clearError = () => {
    setError(null);
    if (saveStatus === 'error') {
      setSaveStatus('idle');
    }
  };

  return {
    saveStatus,
    lastSaved,
    error,
    manualSave,
    clearError,
    isIdle: saveStatus === 'idle',
    isSaving: saveStatus === 'saving',
    isSaved: saveStatus === 'saved',
    hasError: saveStatus === 'error'
  };
};